import React, { useState, useCallback } from 'react';
import DataTable, { Pagination } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { useFetch } from '../hooks/useFetch';
import { merchant as merchantApi } from '../services/api';
import { formatSats, formatUSD, formatDate, formatBTCPrice, truncateTxId, explorerLink } from '../utils/format';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

export default function Payments() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchPayments = useCallback(
    () => merchantApi.payments({ page, limit: 20, status: statusFilter }),
    [page, statusFilter]
  );

  const { data, loading, error, refetch } = useFetch(fetchPayments, [page, statusFilter]);

  const handleFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (val) => (
        <span className="text-gray-400 whitespace-nowrap text-xs">{formatDate(val)}</span>
      ),
    },
    {
      key: 'stripe_session_id',
      label: 'Session ID',
      render: (val) => (
        <span className="font-mono text-xs text-gray-400 truncate max-w-28 block" title={val}>
          {val ? val.slice(0, 18) + '…' : '—'}
        </span>
      ),
    },
    {
      key: 'amount_cents',
      label: 'Amount (USD)',
      sortable: true,
      render: (val) => <span className="font-medium text-gray-200">{formatUSD(val)}</span>,
    },
    {
      key: 'amount_sats',
      label: 'Amount (sats)',
      sortable: true,
      render: (val) => <span className="font-mono text-gray-300">{formatSats(val)}</span>,
    },
    {
      key: 'btc_price_usd',
      label: 'BTC Price',
      sortable: true,
      render: (val) => <span className="text-gray-400">{formatBTCPrice(val)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => <StatusBadge status={val} />,
    },
    {
      key: 'tx_id',
      label: 'TX ID',
      render: (val) =>
        val ? (
          <a
            href={explorerLink(val)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs text-blue-400 hover:text-blue-300 transition-colors"
            title={val}
          >
            {truncateTxId(val)}
          </a>
        ) : (
          <span className="text-gray-600 text-xs">—</span>
        ),
    },
  ];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Payments</h1>
          <p className="text-sm text-gray-400 mt-1">
            {data?.total != null ? `${data.total} total payments` : 'Payment history'}
          </p>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-800 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400" htmlFor="status-filter">Status</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={handleFilterChange}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {statusFilter && (
            <button
              onClick={() => { setStatusFilter(''); setPage(1); }}
              className="text-xs text-gray-500 hover:text-gray-200 transition-colors"
            >
              Clear filter
            </button>
          )}
        </div>

        <DataTable
          columns={columns}
          data={data?.payments || []}
          loading={loading}
          error={error}
          onRetry={refetch}
          emptyMessage="No payments found for the selected filters."
        />

        <Pagination
          page={page}
          pages={data?.pages || 1}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
